import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getHealth } from "../lib/api";
import { relativeTime } from "../lib/format";
import type { Health } from "../lib/types";

type Poll =
  | { phase: "loading" }
  | { phase: "ok"; health: Health }
  | { phase: "error" };

const POLL_MS = 60_000;
const STALE_AFTER_MS = 30 * 60_000;

/** A run that is still going has no finish time yet, so fall back to its start. */
function lastRunAt(lastRun: Health["lastRun"]): string | null {
  if (!lastRun) return null;
  return lastRun.finishedAt ?? lastRun.startedAt ?? null;
}

/**
 * Small footer: the two links out, plus a live "last polled" readout so you can
 * tell at a glance whether the watcher behind this page is actually running.
 */
export default function Footer() {
  const [poll, setPoll] = useState<Poll>({ phase: "loading" });

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    async function load() {
      try {
        const health = await getHealth(controller.signal);
        if (alive) setPoll({ phase: "ok", health });
      } catch {
        // The abort lands here too; the `alive` guard keeps it from painting.
        if (alive) setPoll({ phase: "error" });
      }
    }

    void load();
    const timer = window.setInterval(load, POLL_MS);

    return () => {
      alive = false;
      controller.abort();
      window.clearInterval(timer);
    };
  }, []);

  return (
    <footer className="ap-footer">
      <div className="ap-footer__links">
        <Link to="/feed" className="ap-footer__link">
          Info
        </Link>
        <Link to="/admin" className="ap-footer__link">
          STATUS
        </Link>
      </div>

      <p className="ap-muted" aria-live="polite">
        {renderStatus(poll)}
      </p>
    </footer>
  );
}

function renderStatus(poll: Poll) {
  if (poll.phase === "loading") {
    return "checking the watcher…";
  }

  if (poll.phase === "error") {
    return (
      <>
        <span className="ap-footer__dot ap-footer__dot--stale" aria-hidden="true" />
        watcher unreachable
      </>
    );
  }

  const { health } = poll;
  const at = lastRunAt(health.lastRun);
  const parsed = at ? Date.parse(at) : NaN;
  const known = Number.isFinite(parsed);
  const ago = known ? relativeTime(at) : null;
  const stale = known ? Date.now() - parsed > STALE_AFTER_MS : true;
  const count = health.productCount;

  return (
    <>
      <span
        className={`ap-footer__dot${stale ? " ap-footer__dot--stale" : ""}`}
        aria-hidden="true"
      />
      {ago ? `last polled ${ago}` : "never polled"}
      {typeof count === "number" ? ` · ${count} products watched` : null}
    </>
  );
}
